import React, { useEffect, useRef, useState, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import H2 from '../../../components/Titles/h2';
import Link from '../../../components/Links/link';
import ImageBackground from '../../../components/Images/image-background';
import LogoIconOpaline from '../../../components/Logos/logo-icon-opaline';
import Icon from '../../../components/Images/icon';
import H3 from '../../../components/Titles/h3';
import DescriptionSplit from '../../../components/Description/description-split';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import Opaline1 from '../../../assets/images/opaline/house.jpg';
import Opaline2 from '../../../assets/images/opaline/terrace.jpg';
import Opaline3 from '../../../assets/images/opaline/kitchen.jpg';
import Opaline4 from '../../../assets/images/opaline/living.jpg';
import Opaline5 from '../../../assets/images/opaline/room.jpg';

const imageOpalinePaths = [Opaline1, Opaline2, Opaline3, Opaline4, Opaline5];

const homeIconsContext = require.context('../../../assets/icons/opaline', false, /\.svg$/);

const homeIcons = homeIconsContext.keys().reduce((acc, key) => {
    const iconName = key.replace('./', '').replace('.svg', '');
    const iconModule = homeIconsContext(key);

    acc[iconName] = iconModule.default || iconModule;

    return acc;
}, {});

const Opaline = () => {
    const { t } = useTranslation();

    const sectionData = t('home.sectionOpaline', { returnObjects: true });
    const {
        title,
        subdescription,
        description,
        discover,
        icons = [],
    } = sectionData;

    const leftColumnRef = useRef(null);
    const [leftColumnHeight, setLeftColumnHeight] = useState(null);
    const sliderRef = useRef(null);

    const loadedIcons = useMemo(() => {
        const iconsMap = {};

        icons.forEach((item) => {
            iconsMap[item.icon] = homeIcons[item.icon] || null;

            if (!homeIcons[item.icon]) {
                console.error(`Icon not found: ${item.icon}.svg`);
            }
        });

        return iconsMap;
    }, [icons]);

    useEffect(() => {
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.target === leftColumnRef.current) {
                    const height = entry.contentRect.height;
                    const newHeight = window.innerWidth < 768 ? '100vw' : `${height}px`;
                    setLeftColumnHeight((prev) => (prev !== newHeight ? newHeight : prev));
                }
            }
        });

        if (leftColumnRef.current) {
            resizeObserver.observe(leftColumnRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const settings = {
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        dots: false,
        lazyLoad: 'ondemand',
        adaptiveHeight: true,
    };

    return (
        <div className="bg-gray-50 pt-6 md:pt-20 pb-12 sm:px-8 md:px-24 flex flex-col lg:flex-row items-center">
            <div
                ref={leftColumnRef}
                className="bg-white lg:w-2/5 flex flex-col items-start text-left border border-main shadow-2xl p-8"
            >
                <div className="border-cta-lines border-cta-lines-2 text-main m-auto">
                    <div className="mx-6">
                        <LogoIconOpaline width={70} height={70} />
                    </div>
                </div>

                <div className="w-full text-center my-6">
                    <H2 title={title} fontWeight="bold" theme="description" className="tk-grafolita-script tracking-[12px] opacity-70 mb-4" />
                    <H3
                        title={subdescription}
                        className="mt-2"
                        fontWeight="bold"
                        theme='secondary'
                    />
                </div>

                <DescriptionSplit description={description} />

                <div className="w-full text-center mt-12">
                    <Link
                        link="https://lamaisonopaline.com"
                        externalLink={true}
                        textColor="main"
                        disableHoverEffect={true}
                        className="inline-block"
                    >
                        <span className="group relative inline-block border-cta-lines text-main py-2 px-6">
                            {discover}

                            <span className="absolute bottom-1 left-6 right-6 h-[1px] bg-main scale-x-0 group-hover:scale-x-100 transform transition-transform ease-out duration-300 origin-left" />
                        </span>
                    </Link>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 w-full">
                    {icons.map((item, index) => (
                        <div key={index} className="flex flex-col items-center gap-2">
                            {loadedIcons[item.icon] ? (
                                <Icon
                                    icon={loadedIcons[item.icon]}
                                    alt={item.text}
                                    maxHeight="32px"
                                    maxWidth="32px"
                                    className="opacity-90 w-[34px] h-[34px]"
                                />
                            ) : (
                                <div className="w-[32px] h-[32px] bg-gray-200" />
                            )}
                            <span className="text-gray-700 text-sm text-center">
                                {item.text}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="w-full lg:w-3/5">
                <div className="relative w-full h-full" style={{ height: leftColumnHeight, willChange: 'transform' }}>
                    <Slider ref={sliderRef} {...settings}>
                        {imageOpalinePaths.map((image, idx) => (
                            <ImageBackground
                                key={idx}
                                imageUrl={image}
                                alt={title}
                                customHeight={leftColumnHeight}
                                divClassName="relative h-full w-full image-border-effect"
                                className="shadow-lg"
                            />
                        ))}
                    </Slider>

                    <div className="absolute inset-x-0 top-1/2 transform -translate-y-1/2 flex justify-between pointer-events-none">
                        <button
                            onClick={() => sliderRef.current?.slickPrev()}
                            className="bg-main text-white text-2xl py-1 px-2 flex items-center pointer-events-auto"
                        >
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <button
                            onClick={() => sliderRef.current?.slickNext()}
                            className="bg-main text-white text-2xl py-1 px-2 flex items-center pointer-events-auto"
                        >
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default memo(Opaline);